import { useState, useEffect, useRef } from 'react';
import { X, Calendar, Clock, Loader2 } from 'lucide-react';
import { jobService } from '../../services/jobService';
import { cn } from '../../utils/cn';

export default function InstanceSchedulePopover({
  instanceId,
  instanceDisplayId,
  currentDate,
  currentTime,
  onUpdated,
  onClose,
  position // { top, left } for positioning
}) {
  const [scheduledDate, setScheduledDate] = useState(currentDate || '');
  const [scheduledTime, setScheduledTime] = useState(currentTime || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const popoverRef = useRef(null);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e?.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSave = async () => {
    if (!scheduledDate) return;

    setIsUpdating(true);
    try {
      // Use updateJobInstanceStatus with null status to only update date/time
      await jobService.updateJobInstanceStatus(
        instanceId,
        null, // Keep current status
        {
          scheduled_date: scheduledDate,
          scheduled_time: scheduledTime || null
        }
      );

      onUpdated?.({
        id: instanceId,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime
      });
      onClose?.();
    } catch (err) {
      console.error('Failed to update instance schedule:', err);
      alert(`Failed to update: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Calculate position to ensure popover stays in viewport
  const popoverStyle = {
    position: 'fixed',
    top: position?.top || 0,
    left: position?.left || 0,
    zIndex: 9999
  };

  return (
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="w-72 bg-background border border-border rounded-lg shadow-xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-sm font-medium text-text-primary">
          Edit Schedule - {instanceDisplayId}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Form */}
      <div className="p-3 space-y-3">
        {/* Date */}
        <div>
          <label className="block text-xs text-text-secondary mb-1">Date</label>
          <div className="relative">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e?.target?.value || '')}
              autoFocus
              className="w-full pl-8 pr-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Time */}
        <div>
          <label className="block text-xs text-text-secondary mb-1">Time</label>
          <div className="relative">
            <Clock className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e?.target?.value || '')}
              className="w-full pl-8 pr-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-border flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!scheduledDate || isUpdating}
          className={cn(
            "px-3 py-1.5 text-sm rounded bg-primary-orange text-white transition-colors",
            (!scheduledDate || isUpdating) ? "opacity-50 cursor-not-allowed" : "hover:bg-primary-600"
          )}
        >
          {isUpdating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
}
